import React, { useState, useEffect, useCallback, useRef } from "react";
import "./NetworkStatusModal.css";

// Pure helper functions declared outside the component to avoid hook dependency noise
const checkConnectionQuality = () => {
  if (!navigator.onLine) {
    return "offline";
  }

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (connection) {
    const type = connection.effectiveType;
    const rtt = connection.rtt;
    const downlink = connection.downlink;

    if (type === "slow-2g" || type === "2g" || rtt > 1500 || downlink < 0.5) {
      return "weak";
    }
  }
  return "online";
};

const pingServer = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2500);

    await fetch("/favicon.ico", {
      cache: "no-store",
      mode: "no-cors",
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return true;
  } catch (e) {
    return false;
  }
};

export default function NetworkStatusModal() {
  const [status, setStatus] = useState("online"); // "online" | "offline" | "weak"
  const [showSuccess, setShowSuccess] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Keep a mutable ref of current status to prevent stale closures in event listeners
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // Reset dismissal when network status changes
  useEffect(() => {
    setIsDismissed(false);
  }, [status]);

  const triggerSuccessTransition = useCallback(() => {
    setStatus("online");
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 2500); // Display success message for 2.5s before hiding
  }, []);

  const handleConnectionChange = useCallback(() => {
    const quality = checkConnectionQuality();
    const currentStatus = statusRef.current;
    if (quality === "online" && (currentStatus === "offline" || currentStatus === "weak")) {
      triggerSuccessTransition();
    } else if (quality !== "online") {
      setStatus(quality);
      setShowSuccess(false);
    }
  }, [triggerSuccessTransition]);

  useEffect(() => {
    // Initial check
    const current = checkConnectionQuality();
    setStatus(current);

    const handleOnline = async () => {
      // Confirm connection with a ping
      const ok = await pingServer();
      if (ok) {
        const quality = checkConnectionQuality();
        if (quality === "online") {
          triggerSuccessTransition();
        } else {
          setStatus(quality);
        }
      }
    };

    const handleOffline = () => {
      setStatus("offline");
      setShowSuccess(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      connection.addEventListener("change", handleConnectionChange);
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (connection) {
        connection.removeEventListener("change", handleConnectionChange);
      }
    };
  }, [handleConnectionChange, triggerSuccessTransition]);

  // Monitor status changes for background auto-pinging when offline or weak
  useEffect(() => {
    let intervalId;

    if (status === "offline" || status === "weak") {
      intervalId = setInterval(async () => {
        const ok = await pingServer();
        if (ok) {
          const quality = checkConnectionQuality();
          if (quality === "online") {
            triggerSuccessTransition();
          } else {
            setStatus(quality);
          }
        } else {
          setStatus("offline");
        }
      }, 4000); // Check every 4 seconds in the background
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [status, triggerSuccessTransition]);

  // Draggable State & Handlers
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    // Only drag with left click and avoid close button triggers
    if (e.button !== 0 || e.target.classList.contains("network-capsule-close-btn")) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
    e.preventDefault();
  };

  const handleTouchStart = (e) => {
    if (e.touches.length !== 1 || e.target.classList.contains("network-capsule-close-btn")) return;
    setIsDragging(true);
    const touch = e.touches[0];
    dragStart.current = {
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    };
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const newX = e.clientX - dragStart.current.x;
      const newY = e.clientY - dragStart.current.y;
      setPosition({ x: newX, y: newY });
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      const newX = touch.clientX - dragStart.current.x;
      const newY = touch.clientY - dragStart.current.y;
      setPosition({ x: newX, y: newY });
    };

    const handleDragEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleDragEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging]);

  if ((status === "online" && !showSuccess) || isDismissed) return null;

  return (
    <div 
      className={`network-light-capsule-container ${isDragging ? "dragging" : ""} ${status === "offline" ? "offline" : status === "weak" ? "weak" : "connected"}`}
      style={{ 
        transform: `translate(calc(-50% + ${position.x}px), ${position.y}px)`,
        animation: (isDragging || position.x !== 0 || position.y !== 0) ? 'none' : undefined
      }}
    >
      <div 
        className="network-light-capsule"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="network-capsule-left">
          <div className="network-status-dot">
            <span className="dot-pulse"></span>
          </div>
          <span className="network-capsule-text">
            {status === "offline" && "No Connection • Retrying..."}
            {status === "weak" && "Unstable Connection"}
            {showSuccess && "Connection Restored"}
          </span>
        </div>

        <div className="network-capsule-right">
          <div className="network-capsule-icon">
            {(status === "offline" || status === "weak") && (
              <svg className="spinning-loader" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                <circle cx="12" cy="12" r="10" stroke="rgba(0,0,0,0.06)"></circle>
                <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"></path>
              </svg>
            )}
            {showSuccess && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            )}
          </div>
          <button
            className="network-capsule-close-btn"
            onClick={() => setIsDismissed(true)}
            aria-label="Close notification"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
